import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/categories';
import { useState } from 'react';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug);
  const articles = getArticlesByCategory(slug);
  const [searchTerm, setSearchTerm] = useState('');

  const displayName = currentCategory?.name || '全部作品';
  const displayDesc = currentCategory?.description || '浏览水野朝阳的全部作品合集';

  const filteredArticles = searchTerm.trim()
    ? articles.filter(
        (a) =>
          a.title.includes(searchTerm) ||
          a.excerpt.includes(searchTerm) ||
          a.tags.some((t) => t.includes(searchTerm))
      )
    : articles;

  const categorySchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${displayName} - 水野朝阳官方粉丝站`,
      description: displayDesc,
      url: `https://zfejxdp.cn/category/${slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '水野朝阳官方粉丝站',
        url: 'https://zfejxdp.cn',
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: filteredArticles.map((a, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Article',
            name: a.title,
            url: `https://zfejxdp.cn/article/${a.id}`,
          },
        })),
      },
    },
  ];

  return (
    <>
      <SEOHead
        title={`${displayName}`}
        description={displayDesc}
        keywords={`水野朝阳,${displayName},水野朝阳作品,水野朝阳${displayName}`}
        canonicalPath={`/category/${slug}`}
        ogType="website"
        schema={categorySchema}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '作品浏览', path: '/category/all' },
                ...(slug !== 'all' ? [{ label: displayName }] : []),
              ]}
            />

            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {displayName}
              </h1>
              <p className="text-foreground-500 text-sm">{displayDesc}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`} />
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="relative mb-8">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="text"
                placeholder="搜索文章标题或标签..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 text-sm bg-background-100 border border-background-200/70 rounded-xl focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block" />
                <p className="text-foreground-500 text-sm">没有找到相关内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-accent-200/60 transition-all duration-300"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <div
                        className="w-full h-full bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                        style={{ backgroundImage: `url(${article.image})` }}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-1.5">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs text-foreground-400">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}